import { computed, ref, Ref } from 'vue';
import { IMultipleOptionsMultiSelect } from '@/models/multiselect.interface';
import { IOption } from '@studyportals/multiselect/src/interfaces/options.interface';
import { ActProduct, ProjectProduct, ProjectProductState } from '@studyportals/crm-data-processor-interface';
import actStore from '@/store/act';

export default class ActSelectDashboardsCode {
	public dashboardDropdown: Ref<IMultipleOptionsMultiSelect | null> = ref(null);

	public options = computed((): IOption[] => {
		return actStore.actGetters.actAvailableDashboardsAsOptions();
	});

	public actAvailableDashboards = computed((): ActProduct[] => {
		return actStore.actGetters.actAvailableDashboards();
	});

	public invalidSelectionMessage = computed((): string => {
		if (this.actDashboardsToAdd.value.length) {
			return '';
		}

		return 'Please select at least one dashboard to continue.';
	});

	public amountOfDashboardsBasedSentence = computed((): string => {
		const count = this.actDashboardsToAdd.value.length;
		return `${count} dashboard${count !== 1 ? 's' : ''} selected`;
	});

	public actDashboardsToAdd = computed((): ProjectProduct[] => {
		return actStore.actGetters.actDashboardsToAdd();
	});

	private actDashboardsToAddAsOptions = computed((): IOption[] => {
		return this.actDashboardsToAdd.value
			.map(dashboard => {
				const relevantOption = this.options.value.find((option) => option.value === dashboard.productNumber);
				if (!relevantOption) {
					return;
				}

				return relevantOption;
			}).filter(
				option => option !== undefined
			);
	});

	public mounted(): void {
		if (!this.dashboardDropdown.value) {
			return;
		}

		const relevantOptions = this.actDashboardsToAddAsOptions.value;
		if (!relevantOptions.length) {
			return;
		}

		this.dashboardDropdown.value.selectedOptions = relevantOptions;
	}

	public handleDropdownInput(selectedOptions: IOption[]): void {
		selectedOptions.forEach(this.addDashboardBasedOnOption.bind(this));
		this.removeDashboardsNotInOptions(selectedOptions);
	}

	private addDashboardBasedOnOption(option: IOption): void {
		const alreadyAdded = this.actDashboardsToAdd.value.find(dashboard => dashboard.productNumber === option.value);
		if (alreadyAdded) {
			return;
		}

		const relevantProduct = this.actAvailableDashboards.value.find(dashboard => dashboard.productNumber === option.value);
		if (!relevantProduct) {
			return;
		}

		actStore.actMutations.addActDashboardToAdd({
			projectProductId: '',
			productId: relevantProduct.productId,
			productName: option.label,
			productNumber: option.value as string,
			productStartDate: null,
			productEndDate: null,
			productState: ProjectProductState.Unprocessed
		});
	}

	private removeDashboardsNotInOptions(selectedOptions: IOption[]): void {
		const dashboardsToRemove = this.actDashboardsToAdd.value.filter(dashboard => {
			return !selectedOptions.find(option => option.value === dashboard.productNumber);
		});

		dashboardsToRemove.forEach(this.removeDashboard.bind(this));
	}

	private removeDashboard(dashboard: ProjectProduct): void {
		const index = this.actDashboardsToAdd.value.findIndex(item => item.productNumber === dashboard.productNumber);
		if (index === -1) {
			return;
		}

		actStore.actMutations.removeActDashboardToAdd(index);
	}
}
